// @ts-ignore — highcharts 6.2.0 has no bundled types
import Highcharts from 'highcharts';
// @ts-ignore
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, EmptyState, Tag } from 'sonata-react-components';

import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import { buildChartTooltipHtml, getTooltipRows } from './chartTooltip.helpers';
import { dayTimestamp } from './dayTimestamp.helpers';
import EventsTrack from './EventsTrack/EventsTrack';
import { isWeekendDay } from './weekend.helpers';

import type {
  DoohReportDailyCumulative,
  DoohReportEvent
} from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './coverageFrequencyChart.scss';

const CHART_HEIGHT_PX = 160;
const CHART_LINE_COLOR = 'var(--color-status-pending-600, #2C45D1)';
const WEEKDAY_BAR_COLOR = 'var(--color-sonata-primary-400, #C2D3F1)';
const WEEKEND_BAR_COLOR = 'var(--color-status-error-500, #FF769D)';

type Metric = 'frequency' | 'reach';

const METRICS: Metric[] = ['reach', 'frequency'];

type Props = {
  dailyTimeSeries: DoohReportDailyCumulative[];
  events?: DoohReportEvent[];
  showWeekends?: boolean;
};

type PlotBox = { left: number; width: number; xByTimestamp: Record<string, number> };

function metricValue(point: DoohReportDailyCumulative, metric: Metric): number | null {
  return metric === 'frequency' ? point.frequency : point.reach;
}

function hasDate(point: DoohReportDailyCumulative): point is DoohReportDailyCumulative & { date: string } {
  return point.date !== null;
}

function plotBoxEqual(a: PlotBox, b: PlotBox): boolean {
  if (a.left !== b.left || a.width !== b.width) {
    return false;
  }
  const aKeys = Object.keys(a.xByTimestamp);
  const bKeys = Object.keys(b.xByTimestamp);
  return aKeys.length === bKeys.length && aKeys.every(key => a.xByTimestamp[key] === b.xByTimestamp[key]);
}

export default function CoverageFrequencyChart({
  dailyTimeSeries,
  events = [],
  showWeekends = false
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [metric, setMetric] = useState<Metric>('reach');
  const [plotBox, setPlotBox] = useState<PlotBox | null>(null);
  // The legend switches show / hide the impressions bars and the metric line.
  const [seriesVisibility, setSeriesVisibility] = useState({ bars: true, line: true });
  const chartComponentRef = useRef<any>(null);
  const { formatAbbreviated, formatDecimal, formatPercent } = useDoohReportFormatters();

  const metricLabel: Record<Metric, string> = {
    frequency: formatMessage({
      defaultMessage: 'Frequency',
      id: 'doohReport.coverageFrequencyChart.metric.frequency'
    }),
    reach: formatMessage({ defaultMessage: 'Reach', id: 'doohReport.coverageFrequencyChart.metric.reach' })
  };

  const chartLabel: Record<Metric, string> = {
    frequency: formatMessage({
      defaultMessage: 'Cumulative frequency',
      id: 'doohReport.coverageFrequencyChart.chartLabel.frequency'
    }),
    reach: formatMessage({
      defaultMessage: 'Cumulative target reach',
      id: 'doohReport.coverageFrequencyChart.chartLabel.reach'
    })
  };

  const tooltipLabels = {
    coverage: formatMessage({ defaultMessage: 'Coverage', id: 'doohReport.coverageFrequencyChart.tooltip.coverage' }),
    frequency: metricLabel.frequency,
    reach: metricLabel.reach
  };
  const tooltipFormat = {
    coverage: (value: number) =>
      formatMessage(
        { defaultMessage: '{value} of Target Universe', id: 'doohReport.coverageFrequencyChart.tooltip.coverageValue' },
        { value: formatPercent(value, 1) }
      ),
    frequency: (value: number) =>
      formatMessage(
        { defaultMessage: '{value} impacts / person', id: 'doohReport.coverageFrequencyChart.tooltip.frequencyValue' },
        { value: formatDecimal(value, 1) }
      ),
    reach: (value: number) =>
      formatMessage(
        { defaultMessage: '{value} people', id: 'doohReport.coverageFrequencyChart.tooltip.reachValue' },
        { value: formatAbbreviated(value) }
      )
  };

  const impressionsLabel = formatMessage({
    defaultMessage: 'Impressions',
    id: 'doohReport.coverageFrequencyChart.impressions'
  });

  const formatMetricValue = (value: number): string => {
    if (effectiveMetric === 'frequency') {
      return formatDecimal(value, 2);
    }

    return formatAbbreviated(value);
  };

  const formatImpressions = (value: number): string => formatAbbreviated(value);

  const datedSeries = dailyTimeSeries.filter(hasDate);
  const availableMetrics = METRICS.filter(candidate =>
    datedSeries.some(point => metricValue(point, candidate) !== null)
  );
  const effectiveMetric: Metric = (availableMetrics.includes(metric) ? metric : availableMetrics[0]) ?? metric;

  const tickPositions = datedSeries.map(point => dayTimestamp(point.date));
  const pointByX = new Map(datedSeries.map(point => [dayTimestamp(point.date), point]));

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) {
      return undefined;
    }
    const updatePlotBox = () => {
      const axis = chart.xAxis[0];
      const xByTimestamp: Record<number, number> = {};
      for (const tick of (axis.tickPositions ?? []) as number[]) {
        xByTimestamp[tick] = axis.toPixels(tick, true);
      }
      const next: PlotBox = { left: chart.plotLeft, width: chart.plotWidth, xByTimestamp };
      setPlotBox(prev => (prev && plotBoxEqual(prev, next) ? prev : next));
    };
    // Keeps a tooltip visible by default (and whenever the mouse leaves the chart) by
    // forcing the shared tooltip back to the last day's points, same as a normal hover would.
    const showLastDayTooltip = () => {
      const lastPoints = [];
      for (const series of chart.series) {
        // A series hidden from the legend switches has no points.
        if (!series.visible || !series.points) {
          continue;
        }
        const lastPoint = series.points[series.points.length - 1];
        if (lastPoint && lastPoint.y !== null) {
          lastPoints.push(lastPoint);
        }
      }
      if (lastPoints.length > 0) {
        chart.tooltip.refresh(lastPoints);
      } else {
        chart.tooltip.hide(0);
      }
    };
    updatePlotBox();
    showLastDayTooltip();
    const unbindRedraw = Highcharts.addEvent(chart, 'redraw', () => {
      updatePlotBox();
      showLastDayTooltip();
    });
    const container = chart.container;
    container.addEventListener('mouseleave', showLastDayTooltip);
    return () => {
      unbindRedraw();
      container.removeEventListener('mouseleave', showLastDayTooltip);
    };
  }, []);

  const chartOptions = {
    // No native x-axis labels left to reserve room for (EventsTrack draws its
    // own), so the default bottom spacing would mostly be dead air above it —
    // but a y-axis label is vertically centered on its tick, so the lowest one
    // still needs a few px below the plot area or its bottom half gets clipped.
    chart: { animation: false, backgroundColor: 'transparent', height: CHART_HEIGHT_PX, spacingBottom: 8 },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: {
      column: { borderRadius: 2, groupPadding: 0.1, pointPadding: 0.05 },
      series: { states: { hover: { brightness: -0.1 } } },
      spline: { marker: { enabled: false } }
    },
    series: [
      {
        color: WEEKDAY_BAR_COLOR,
        data: datedSeries.map(point => ({
          color: isWeekendDay(point.date) ? WEEKEND_BAR_COLOR : WEEKDAY_BAR_COLOR,
          x: dayTimestamp(point.date),
          y: point.impressions
        })),
        name: impressionsLabel,
        type: 'column',
        visible: seriesVisibility.bars,
        yAxis: 0
      },
      {
        // `spline`, not `areaspline`: only the stroke itself gets the line's
        // color — no filled area underneath it.
        color: CHART_LINE_COLOR,
        data: datedSeries.map(point => [dayTimestamp(point.date), metricValue(point, effectiveMetric)]),
        lineWidth: 2,
        name: metricLabel[effectiveMetric],
        type: 'spline',
        visible: seriesVisibility.line,
        yAxis: 1
      }
    ],
    title: { text: null },
    tooltip: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      formatter(this: any): string {
        const point = pointByX.get(this.x);
        if (!point) {
          return '';
        }
        return buildChartTooltipHtml({
          rows: getTooltipRows(point, tooltipLabels, tooltipFormat),
          title: `${moment.utc(point.date).format('MMM D')} · ${chartLabel[effectiveMetric]}`
        });
      },
      shadow: false,
      shared: true,
      useHTML: true
    },
    xAxis: {
      labels: { enabled: false },
      lineWidth: 0,
      plotBands: showWeekends && datedSeries.length > 0 ? buildWeekendBands(datedSeries) : [],
      tickLength: 0,
      tickPositions,
      type: 'datetime'
    },
    yAxis: [
      {
        gridLineColor: 'var(--color-sonata-primary-300, #DDE9FD)',
        labels: {
          formatter(this: any): string {
            return formatImpressions(this.value);
          }
        },
        min: 0,
        title: { text: null }
      },
      {
        gridLineWidth: 0,
        labels: {
          formatter(this: any): string {
            return formatMetricValue(this.value);
          }
        },
        min: 0,
        opposite: true,
        title: { text: null },
        visible: false
      }
    ]
  };

  if (datedSeries.length === 0 || availableMetrics.length === 0) {
    return (
      <div className="coverageFrequencyChart">
        <div className="coverageFrequencyChart__box">
          <EmptyState
            description={formatMessage({
              defaultMessage: 'There is no coverage & frequency data for this campaign yet.',
              id: 'doohReport.coverageFrequencyChart.empty.description'
            })}
            show
            title={formatMessage({
              defaultMessage: 'No data available',
              id: 'doohReport.coverageFrequencyChart.empty.title'
            })}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="coverageFrequencyChart">
      <div className="coverageFrequencyChart__box">
        <div className="coverageFrequencyChart__periodBar">
          <div className="coverageFrequencyChart__chartLabel">
            <span className="coverageFrequencyChart__chartLabelIcon fa-solid fa-chart-mixed" />
            {chartLabel[effectiveMetric]}
          </div>
          <div className="coverageFrequencyChart__legendAndTags">
            <div className="coverageFrequencyChart__legend">
              <Checkbox
                active={seriesVisibility.bars}
                classes="coverageFrequencyChart__seriesSwitch"
                id="coverageFrequencyChart-barsSwitch"
                onClickAction={(_id: string, checked: boolean) =>
                  setSeriesVisibility(prev => ({ ...prev, bars: checked }))
                }
                tiny
                type="switch"
              />
              <span className="coverageFrequencyChart__legendItem">
                <span className="coverageFrequencyChart__legendDot" style={{ backgroundColor: WEEKDAY_BAR_COLOR }} />
                {formatMessage({
                  defaultMessage: 'Weekday impressions',
                  id: 'doohReport.coverageFrequencyChart.legend.weekday'
                })}
              </span>
              <span className="coverageFrequencyChart__legendItem">
                <span className="coverageFrequencyChart__legendDot" style={{ backgroundColor: WEEKEND_BAR_COLOR }} />
                {formatMessage({
                  defaultMessage: 'Weekend impressions',
                  id: 'doohReport.coverageFrequencyChart.legend.weekend'
                })}
              </span>
              <Checkbox
                active={seriesVisibility.line}
                classes="coverageFrequencyChart__seriesSwitch"
                id="coverageFrequencyChart-lineSwitch"
                onClickAction={(_id: string, checked: boolean) =>
                  setSeriesVisibility(prev => ({ ...prev, line: checked }))
                }
                tiny
                type="switch"
              />
              <span className="coverageFrequencyChart__legendItem">
                <span className="coverageFrequencyChart__legendLine" style={{ backgroundColor: CHART_LINE_COLOR }} />
                {chartLabel[effectiveMetric]}
              </span>
            </div>
            <div className="coverageFrequencyChart__metricTags">
              {availableMetrics.map(option => (
                <Tag
                  id={`coverageFrequencyChart-metric-${option}`}
                  key={option}
                  onClick={() => setMetric(option)}
                  status={effectiveMetric === option ? 'enabled' : 'information'}
                >
                  {metricLabel[option]}
                </Tag>
              ))}
            </div>
          </div>
        </div>
        <div className="coverageFrequencyChart__chart">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartComponentRef} />
        </div>
        <EventsTrack dates={datedSeries.map(point => point.date)} events={events} plotBox={plotBox} />
      </div>
    </div>
  );
}

function buildWeekendBands(
  datedSeries: (DoohReportDailyCumulative & { date: string })[]
): { color: string; from: number; to: number }[] {
  const WEEKEND_BAND_COLOR = 'var(--color-sonata-secondary-300, #F3F3F3)';
  const bands: { color: string; from: number; to: number }[] = [];
  let bandStart: number | null = null;
  const cursor = moment.utc(datedSeries[0].date).startOf('day');
  const end = moment.utc(datedSeries[datedSeries.length - 1].date).startOf('day');

  while (cursor.isSameOrBefore(end)) {
    const isWeekend = isWeekendDay(cursor.format('YYYY-MM-DD'));
    if (isWeekend && bandStart === null) {
      bandStart = cursor.valueOf();
    } else if (!isWeekend && bandStart !== null) {
      bands.push({ color: WEEKEND_BAND_COLOR, from: bandStart, to: cursor.valueOf() });
      bandStart = null;
    }
    cursor.add(1, 'day');
  }
  if (bandStart !== null) {
    bands.push({ color: WEEKEND_BAND_COLOR, from: bandStart, to: cursor.valueOf() });
  }

  return bands;
}
